import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import SignatureCanvas from "react-signature-canvas";
import {RiDeleteBin5Line} from "react-icons/ri";
import {formatDateSpelled} from "../../utils/appLocale";
import {
  fetchConsentStatus,
  fetchConsentFormBlobUrl,
  fetchConsentDocumentUrl,
  signConsent,
} from "./consentRequests";

/**
 * Read-and-sign surface for the TELUS Health eClaims patient consent.
 *
 * The patient reads the OFFICIAL form (rendered inline, pre-filled by the
 * server), makes the two consent decisions explicitly, types their printed
 * name and signs on the canvas. What leaves the browser is the signature image
 * and the two booleans — never a document. The server builds and stores the
 * signed PDF, which is why nothing here can misrepresent what was agreed to.
 *
 * The two checkboxes mirror the two independent consents printed on the form:
 * exchanging personal information, and assigning benefits to the clinic. They
 * are separate on paper and stay separate here — the second one decides whether
 * the insurer may pay the clinic directly, so pre-bundling them would put words
 * in the patient's mouth.
 */
export default function ConsentSigner({state, onSigned}) {
  const toast = useToast();
  const patient = state?.patient;
  const pid = patient?.pid;
  const locationId = state?.selectedLocation || null;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [consent, setConsent] = useState(null);
  const [formUrl, setFormUrl] = useState(null);
  const [formError, setFormError] = useState(null);

  const [personalInfoConsent, setPersonalInfoConsent] = useState(false);
  const [benefitAssignment, setBenefitAssignment] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [hasStrokes, setHasStrokes] = useState(false);
  const [saving, setSaving] = useState(false);

  const signatureRef = useRef(null);
  // The object URL is revoked on unmount rather than on every render: revoking
  // while the iframe still points at it blanks the document.
  const formUrlRef = useRef(null);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const data = await fetchConsentStatus(state, pid);
      setStatus(data?.status || null);
      setConsent(data?.consent || null);
      // Default the printed name to the patient's own, since they are the
      // signatory in the common case. Still editable: on a dependant's
      // coverage the plan member signing is someone else.
      setSignerName(
        (previous) =>
          previous ||
          `${patient?.fName || ""} ${patient?.lName || ""}`.trim()
      );
    } catch (error) {
      toast({
        title: "Could not load your consent",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [state, pid, patient, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!pid) return undefined;
    (async () => {
      try {
        const blobUrl = await fetchConsentFormBlobUrl(state, pid, locationId);
        if (cancelled) {
          window.URL.revokeObjectURL(blobUrl);
          return;
        }
        formUrlRef.current = blobUrl;
        setFormUrl(blobUrl);
      } catch (error) {
        if (!cancelled) {
          setFormError(
            error?.response?.data?.message ||
              "We couldn't load the form to display. You can still sign below."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, pid, locationId]);

  useEffect(
    () => () => {
      if (formUrlRef.current) window.URL.revokeObjectURL(formUrlRef.current);
    },
    []
  );

  const alreadySigned = status === "active" && consent?.hasDocument;

  const canSubmit =
    personalInfoConsent && hasStrokes && signerName.trim().length > 1 && !saving;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const signatureImage = signatureRef.current?.toDataURL("image/png");
      const saved = await signConsent(state, {
        pid,
        locationId,
        signatureImage,
        signerName: signerName.trim(),
        personalInfoConsent,
        benefitAssignment,
      });
      toast({
        title: "Consent signed",
        description: "Thank you. Your clinic has been notified.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setConsent(saved);
      setStatus("active");
      onSigned?.(saved);
    } catch (error) {
      toast({
        title: "We couldn't save your signature",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }

  async function openSignedDocument() {
    if (!consent?.consentId) return;
    const result = await fetchConsentDocumentUrl(state, consent.consentId);
    if (result?.url) window.open(result.url, "_blank", "noopener");
  }

  if (loading) {
    return (
      <Flex align="center" justify="center" py={12}>
        <Spinner size="sm" mr={3} />
        <Text fontSize="sm" color="gray.600">
          Loading your consent form…
        </Text>
      </Flex>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Flex align="center" gap={3} wrap="wrap">
          <Heading fontSize="lg" color="#1a1f36">
            Electronic transmission authorization
          </Heading>
          {alreadySigned && (
            <Badge colorScheme="green" fontSize="10px">
              Signed
            </Badge>
          )}
          {status === "stale" && (
            <Badge colorScheme="orange" fontSize="10px">
              Needs to be signed again
            </Badge>
          )}
        </Flex>
        <Text fontSize="13px" color="#6b7280" mt={1}>
          This authorization lets your clinic submit insurance claims
          electronically on your behalf.
        </Text>
      </Box>

      {alreadySigned && (
        <Alert status="success" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            You signed this on{" "}
            {consent?.signedAt
              ? formatDateSpelled(consent.signedAt)
              : ""}
            .{" "}
            <Button variant="link" size="sm" onClick={openSignedDocument}>
              View your signed copy
            </Button>
          </Box>
        </Alert>
      )}

      {status === "stale" && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Your insurance details changed since you last signed, so a new
          signature is needed for your current policy.
        </Alert>
      )}

      <Text fontSize="12px" color="#6b7280">
        Please read the form below. You do not fill anything in on the document
        itself — use the boxes underneath it to agree and sign.
      </Text>
      {/* The official document, so the patient signs what they actually read. */}
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        overflow="hidden"
        bg="gray.50"
      >
        {formUrl ? (
          <Box
            as="iframe"
            src={formUrl}
            title="Electronic transmission authorization and consent form"
            width="100%"
            height="520px"
            border="none"
          />
        ) : (
          <Flex align="center" justify="center" py={10} px={4}>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              {formError || "Loading the form…"}
            </Text>
          </Flex>
        )}
      </Box>

      {!alreadySigned || status === "stale" ? (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              What you are agreeing to
            </Text>
            <VStack align="stretch" spacing={3}>
              <Checkbox
                isChecked={personalInfoConsent}
                onChange={(event) =>
                  setPersonalInfoConsent(event.target.checked)
                }
              >
                <Text fontSize="sm">
                  I authorize the collection, use and exchange of my personal
                  information with my insurer for the administration of my
                  claims.
                </Text>
              </Checkbox>
              <Checkbox
                isChecked={benefitAssignment}
                onChange={(event) => setBenefitAssignment(event.target.checked)}
              >
                <Text fontSize="sm">
                  I assign the benefits payable for my claims to my healthcare
                  provider, so the insurer pays the clinic directly.
                </Text>
              </Checkbox>
            </VStack>
            {!personalInfoConsent && (
              <Text fontSize="xs" color="orange.600" mt={2}>
                The first authorization is required — without it claims cannot
                be submitted electronically.
              </Text>
            )}
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={1}>
              Printed name of the plan member
            </Text>
            <Input
              size="sm"
              bg="white"
              maxW="360px"
              value={signerName}
              onChange={(event) => setSignerName(event.target.value)}
              placeholder="Full name"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              If the plan is held by someone else (for example a parent or
              spouse), enter the plan member's name.
            </Text>
          </Box>

          <Box>
            <Flex align="center" justify="space-between" mb={1}>
              <Text fontSize="sm" fontWeight="semibold">
                Signature
              </Text>
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<RiDeleteBin5Line />}
                onClick={() => {
                  signatureRef.current?.clear();
                  setHasStrokes(false);
                }}
              >
                Clear
              </Button>
            </Flex>
            <Box
              borderWidth="1px"
              borderColor="gray.300"
              borderRadius="md"
              bg="white"
              overflow="hidden"
              maxW="480px"
            >
              <SignatureCanvas
                ref={signatureRef}
                penColor="#111827"
                maxWidth={2.5}
                onEnd={() => setHasStrokes(true)}
                canvasProps={{height: 160, width: 470}}
              />
            </Box>
            {!hasStrokes && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                Sign above using your mouse or finger.
              </Text>
            )}
          </Box>

          <Box>
            <Button
              colorScheme="blue"
              isDisabled={!canSubmit}
              isLoading={saving}
              loadingText="Saving…"
              onClick={handleSubmit}
            >
              Sign and submit
            </Button>
          </Box>
        </VStack>
      ) : null}
    </VStack>
  );
}
